'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Clock, CalendarDays, Check, ArrowRight } from 'lucide-react';
import { getAllCourses, Course } from '@/lib/curriculumData';
import PageHeader from '@/components/PageHeader';
import { LEVELS, SITE } from '@/lib/site';

type LevelFilter = 'all' | keyof typeof LEVELS;

function Curriculum() {
  const params = useSearchParams();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<LevelFilter>('all');

  useEffect(() => {
    setCourses(getAllCourses());
  }, []);

  useEffect(() => {
    const lv = params.get('level');
    if (lv && lv in LEVELS) setSelectedLevel(lv as LevelFilter);
  }, [params]);

  const filters: { id: LevelFilter; name: string }[] = [
    { id: 'all', name: '전체' },
    ...(Object.keys(LEVELS) as (keyof typeof LEVELS)[]).map((id) => ({ id, name: LEVELS[id].name })),
  ];

  const filteredCourses =
    selectedLevel === 'all' ? courses : courses.filter((course) => course.level === selectedLevel);

  const info = [
    { title: '등록 방법', items: ['온라인 수강 신청', '전화 상담 후 등록', '방문 상담 및 등록'] },
    { title: '준비물', items: ['필기도구', '노트', '교재 (첫 수업 시 배부)'] },
    { title: '환불 규정', items: ['개강 전: 전액 환불', '개강 후 1주일 이내: 80% 환불', '개강 후 2주일 이내: 50% 환불'] },
    { title: '문의', items: [`전화: ${SITE.phone}`, `이메일: ${SITE.email}`, '카카오톡: 실시간 상담'] },
  ];

  return (
    <>
      <PageHeader en="Curriculum" title="커리큘럼" description="레벨별 맞춤 교육 과정을 확인하세요." />

      <div className="sticky top-[72px] z-30 border-y border-cream-200 bg-cream/90 backdrop-blur-lg">
        <div className="wrap flex items-center gap-2 overflow-x-auto py-3">
          {filters.map((f) => {
            const count = f.id === 'all' ? courses.length : courses.filter((c) => c.level === f.id).length;
            const on = selectedLevel === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setSelectedLevel(f.id)}
                className={`flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                  on ? 'bg-forest text-white' : 'bg-white text-muted hover:text-ink'
                }`}
              >
                {f.id !== 'all' && <span className={`h-2 w-2 rounded-full ${LEVELS[f.id].dot}`} />}
                {f.name}
                <span className={on ? 'text-white/50' : 'text-muted/50'}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <section className="bg-cream py-12 sm:py-16">
        <div className="wrap">
          {filteredCourses.length === 0 ? (
            <div className="rounded-[28px] border border-dashed border-cream-200 bg-white py-20 text-center text-muted">
              해당 레벨의 과정이 없습니다.
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {filteredCourses.map((course) => {
                const L = LEVELS[course.level];
                return (
                  <article
                    key={course.id}
                    className="group flex flex-col rounded-[28px] border border-cream-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-forest-100 hover:shadow-[0_24px_50px_-28px_rgba(29,59,47,.45)]"
                  >
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${L?.chip}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${L?.dot}`} />
                        {L?.name}
                      </span>
                      <span className="font-serif font-extrabold text-sm text-muted">{L?.en}</span>
                    </div>

                    <h3 className="mt-5 text-2xl font-bold tracking-tight text-ink">{course.name}</h3>
                    <p className="mt-2 text-[15px] text-muted">{course.description}</p>

                    <div className="mt-6 grid grid-cols-[92px_1fr] gap-2 text-sm">
                      <div className="rounded-2xl bg-cream px-4 py-3">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-muted">
                          <CalendarDays className="h-3.5 w-3.5" /> 기간
                        </p>
                        <p className="mt-0.5 font-bold text-ink">{course.duration}</p>
                      </div>
                      <div className="rounded-2xl bg-cream px-4 py-3">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-muted">
                          <Clock className="h-3.5 w-3.5" /> 일정
                        </p>
                        <p className="mt-0.5 font-bold text-ink">
                          {course.schedule}
                        </p>
                      </div>
                    </div>

                    <ul className="mb-8 mt-6 space-y-2.5">
                      {course.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2.5 text-[15px] text-ink">
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forest-50 text-forest-700">
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-auto flex items-end justify-between gap-4 border-t border-cream-100 pt-6">
                      <div>
                        <p className="text-xs font-semibold text-muted">수강료</p>
                        <p className="font-serif font-extrabold text-2xl text-ink">
                          {course.price.toLocaleString()}
                          <span className="ml-0.5 font-sans text-sm font-bold text-muted">원</span>
                        </p>
                      </div>
                      <Link href={`/enrollment?courseId=${course.id}`} className="btn-forest px-5 py-3 text-sm group-hover:bg-coral">
                        신청
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          <div className="mt-16 rounded-[32px] bg-white p-8 sm:p-12">
            <p className="font-serif font-extrabold text-lg text-forest-600">Guide</p>
            <h2 className="mt-1 text-2xl font-bold text-ink sm:text-3xl">수강 안내</h2>
            <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {info.map((b) => (
                <div key={b.title} className="border-t-2 border-forest pt-5">
                  <h3 className="font-bold text-ink">{b.title}</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-muted">
                    {b.items.map((it) => (
                      <li key={it}>{it}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function CurriculumPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-cream" />}>
      <Curriculum />
    </Suspense>
  );
}
